import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { API_BASE } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Save, ArrowLeft, Sparkles, Play, Pause, Volume2, Loader2, Headphones, Mic, Plus, Trash2, ChevronUp, ChevronDown, Wand2, AudioLines, FileUp, FileText, X, Clock, ExternalLink, Copy, Pencil } from 'lucide-react';

const getErrorMsg = (err, fallback = 'Something went wrong') => {
  const d = err?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(e => e.msg || JSON.stringify(e)).join(', ');
  return fallback;
};

/* ─── TTS SETTINGS (shared) ─── */
function TTSSettings({ voices, provider, setProvider, voice, setVoice, model, setModel, speed, setSpeed }) {
  const currentVoices = provider === 'openai' ? voices.openai : voices.elevenlabs;
  const handleProviderChange = (p) => {
    setProvider(p);
    const vList = p === 'openai' ? voices.openai : voices.elevenlabs;
    if (vList.length) setVoice(vList[0].id);
  };
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
      <div className="space-y-1">
        <Label className="text-xs">Provider</Label>
        <Select value={provider} onValueChange={handleProviderChange}>
          <SelectTrigger className="h-9" data-testid="tts-provider-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="openai">OpenAI TTS</SelectItem>
            <SelectItem value="elevenlabs">ElevenLabs</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Voice</Label>
        <Select value={voice} onValueChange={setVoice}>
          <SelectTrigger className="h-9" data-testid="tts-voice-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {currentVoices.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {provider === 'openai' && (
        <>
          <div className="space-y-1">
            <Label className="text-xs">Model</Label>
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tts-1">TTS-1 (Fast)</SelectItem>
                <SelectItem value="tts-1-hd">TTS-1-HD (HD)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Speed: {speed}x</Label>
            <input type="range" min="0.5" max="2.0" step="0.1" value={speed} onChange={e => setSpeed(parseFloat(e.target.value))} className="w-full h-2 mt-2 accent-[#1A9DA5] cursor-pointer" />
          </div>
        </>
      )}
    </div>
  );
}

/* ─── CHAPTER MANAGER ─── */
function ChapterManager({ product, setProduct, productId }) {
  const chapters = product.chaptered_script || [];
  const [voices, setVoices] = useState({ openai: [], elevenlabs: [] });
  const [provider, setProvider] = useState('openai');
  const [voice, setVoice] = useState('alloy');
  const [model, setModel] = useState('tts-1-hd');
  const [speed, setSpeed] = useState(1.0);
  const [generatingChapter, setGeneratingChapter] = useState(null);
  const [generatingAllScript, setGeneratingAllScript] = useState(false);
  const [generatingTTS, setGeneratingTTS] = useState(null);
  const [generatingAllTTS, setGeneratingAllTTS] = useState(false);
  const [redoingAllTTS, setRedoingAllTTS] = useState(false);
  const [playingChapter, setPlayingChapter] = useState(null);
  const audioRef = React.useRef(null);

  useEffect(() => {
    api.get('/tts/voices').then(r => {
      setVoices(r.data);
      if (r.data.openai?.length) setVoice(r.data.openai[0].id);
    }).catch(() => {});
  }, []);

  const updateChapters = (newChapters) => setProduct(prev => ({ ...prev, chaptered_script: newChapters }));

  const addChapter = () => {
    const num = chapters.length + 1;
    updateChapters([...chapters, { chapter: num, title: `Chapter ${num}`, content: '', audio_base64: '', target_length_minutes: '' }]);
  };

  const removeChapter = (i) => {
    const updated = chapters.filter((_, idx) => idx !== i).map((ch, idx) => ({ ...ch, chapter: idx + 1 }));
    updateChapters(updated);
  };

  const moveChapter = (i, dir) => {
    const arr = [...chapters];
    const target = i + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[i], arr[target]] = [arr[target], arr[i]];
    arr.forEach((ch, idx) => { ch.chapter = idx + 1; });
    updateChapters(arr);
  };

  const updateChapter = (i, field, value) => {
    const arr = [...chapters];
    arr[i] = { ...arr[i], [field]: value };
    updateChapters(arr);
  };

  const generateChapterScript = async (i) => {
    if (!productId) { toast.error('Save product first'); return; }
    setGeneratingChapter(i);
    try {
      const ch = chapters[i];
      const { data } = await api.post('/ai/generate-chapter', {
        product_id: productId,
        chapter_index: i,
        chapter_title: ch?.title || '',
        target_length_minutes: ch?.target_length_minutes ? parseFloat(ch.target_length_minutes) : null
      });
      updateChapter(i, 'content', data.content);
      toast.success(`Chapter ${i + 1} generated (${data.word_count} words, ~${data.estimated_minutes} min)`);
    } catch (err) { toast.error(getErrorMsg(err, 'Generation failed')); }
    finally { setGeneratingChapter(null); }
  };

  const generateAllScripts = async () => {
    if (!productId) { toast.error('Save product first'); return; }
    if (!chapters.length) { toast.error('Add chapters first'); return; }
    setGeneratingAllScript(true);
    try {
      toast.info('Generating all chapter scripts with AI...');
      const { data } = await api.post('/ai/generate-all-chapters', { product_id: productId });
      setProduct(prev => ({ ...prev, chaptered_script: data.chaptered_script || prev.chaptered_script }));
      toast.success('All chapter scripts generated!');
    } catch (err) { toast.error(getErrorMsg(err, 'Generation failed')); }
    finally { setGeneratingAllScript(false); }
  };

  const generateChapterTTS = async (i) => {
    if (!productId) return;
    setGeneratingTTS(i);
    try {
      toast.info(`Generating audio for chapter ${i + 1}...`);
      await api.post('/tts/generate-chapter', { product_id: productId, chapter_index: i, provider, voice, model, speed });
      // Refresh product from server to get the updated chaptered_script with audio_asset_id
      const { data: updated } = await api.get(`/products/${productId}`);
      setProduct(prev => ({ ...prev, chaptered_script: updated.chaptered_script || prev.chaptered_script }));
      toast.success(`Chapter ${i + 1} audio generated!`);
    } catch (err) { toast.error(getErrorMsg(err, 'TTS failed')); }
    finally { setGeneratingTTS(null); }
  };

  const generateAllTTS = async () => {
    if (!productId) return;
    setGeneratingAllTTS(true);
    try {
      toast.info('Generating audio for all chapters...');
      const { data } = await api.post('/tts/generate-all-chapters', { product_id: productId, chapter_index: 0, provider, voice, model, speed });
      if (data.product) setProduct(prev => ({ ...prev, chaptered_script: data.product.chaptered_script || prev.chaptered_script }));
      toast.success(`Audio generated for ${data.generated_chapters.length} chapters!`);
    } catch (err) { toast.error(getErrorMsg(err, 'TTS generation failed')); }
    finally { setGeneratingAllTTS(false); }
  };

  const redoAllTTS = async () => {
    if (!productId) return;
    if (!window.confirm(`Regenerate audio for all ${chaptersWithContent} chapters? This will replace existing audio.`)) return;
    setRedoingAllTTS(true);
    try {
      toast.info('Regenerating all chapter audio...');
      const { data } = await api.post('/tts/redo-all-chapters', { product_id: productId, chapter_index: 0, provider, voice, model, speed });
      if (data.product) setProduct(prev => ({ ...prev, chaptered_script: data.product.chaptered_script || prev.chaptered_script }));
      toast.success(`Audio regenerated for ${data.generated_chapters.length} chapters!`);
    } catch (err) { toast.error(getErrorMsg(err, 'TTS generation failed')); }
    finally { setRedoingAllTTS(false); }
  };

  const playChapterAudio = (i) => {
    const ch = chapters[i];
    if (!ch?.audio_base64 && !ch?.audio_asset_id) return;
    if (playingChapter === i) { audioRef.current?.pause(); setPlayingChapter(null); return; }
    if (audioRef.current) {
      const src = ch.audio_asset_id ? `${API_BASE}/assets/${ch.audio_asset_id}/audio` : `data:audio/mp3;base64,${ch.audio_base64}`;
      audioRef.current.src = src;
      audioRef.current.play();
      setPlayingChapter(i); }
  };

  const totalWords = chapters.reduce((sum, ch) => sum + (ch.content?.split(/\s+/).filter(Boolean).length || 0), 0);
  const chaptersWithAudio = chapters.filter(ch => ch.audio_base64 || ch.audio_asset_id).length;
  const chaptersWithContent = chapters.filter(ch => ch.content?.trim()).length;
  const missingAudio = chaptersWithContent - chaptersWithAudio;

  return (
    <div className="space-y-4" data-testid="chapter-manager">
      <audio ref={audioRef} onEnded={() => setPlayingChapter(null)} />

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline">{chapters.length} chapters</Badge>
          <Badge variant="outline">{totalWords} words / ~{Math.round(totalWords / 145)} min</Badge>
          <Badge variant="outline" className={chaptersWithContent === chapters.length && chapters.length > 0 ? 'bg-green-500/10 text-green-600' : ''}>{chaptersWithContent}/{chapters.length} scripted</Badge>
          <Badge variant="outline" className={chaptersWithAudio === chapters.length && chapters.length > 0 ? 'bg-blue-500/10 text-blue-600' : ''}><AudioLines className="w-3 h-3 mr-1" />{chaptersWithAudio}/{chapters.length} audio</Badge>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={generateAllScripts} disabled={generatingAllScript || !chapters.length} data-testid="generate-all-scripts-btn">
            {generatingAllScript ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1 text-[#E8735A]" />}
            {generatingAllScript ? 'Generating...' : 'AI Write All'}
          </Button>
          <Button variant="outline" size="sm" onClick={addChapter} data-testid="add-chapter-btn"><Plus className="w-3.5 h-3.5 mr-1" />Add Chapter</Button>
        </div>
      </div>

      <Card>
        <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-2"><Mic className="w-4 h-4 text-[#1A9DA5]" />TTS Settings</CardTitle></CardHeader>
        <CardContent className="pb-3 space-y-3">
          <TTSSettings voices={voices} provider={provider} setProvider={setProvider} voice={voice} setVoice={setVoice} model={model} setModel={setModel} speed={speed} setSpeed={setSpeed} />
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={generateAllTTS} disabled={generatingAllTTS || redoingAllTTS || missingAudio <= 0} data-testid="generate-all-tts-btn">
              {generatingAllTTS ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Volume2 className="w-3.5 h-3.5 mr-1 text-[#1A9DA5]" />}
              {generatingAllTTS ? 'Generating...' : `Generate Audio (${missingAudio} remaining)`}
            </Button>
            <Button variant="outline" size="sm" onClick={redoAllTTS} disabled={generatingAllTTS || redoingAllTTS || chaptersWithContent <= 0} data-testid="redo-all-tts-btn">
              {redoingAllTTS ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Volume2 className="w-3.5 h-3.5 mr-1 text-[#E8735A]" />}
              {redoingAllTTS ? 'Regenerating...' : `Redo All Audio (${chaptersWithContent})`}
            </Button>
          </div>
        </CardContent>
      </Card>

      {chapters.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-muted-foreground">
          <Headphones className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium mb-1">No chapters yet</p>
          <p className="text-sm mb-4">Add chapters to structure your audio guide content.</p>
          <Button onClick={addChapter} className="bg-[#1A9DA5] hover:bg-[#158D92] text-white"><Plus className="w-4 h-4 mr-1" />Add First Chapter</Button>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {chapters.map((ch, i) => {
            const wordCount = ch.content?.split(/\s+/).filter(Boolean).length || 0;
            const hasAudio = !!(ch.audio_base64 || ch.audio_asset_id);
            const hasContent = !!ch.content?.trim();
            return (
              <Card key={i} className={`transition-all ${playingChapter === i ? 'border-[#1A9DA5] shadow-md' : ''}`} data-testid={`chapter-card-${i}`}>
                <div className="flex items-center gap-2 px-4 py-3 border-b bg-muted/30">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${hasContent ? 'bg-[#1A9DA5]/10 text-[#1A9DA5]' : 'bg-muted text-muted-foreground'}`}>{ch.chapter || i + 1}</div>
                  <Input value={ch.title || ''} onChange={e => updateChapter(i, 'title', e.target.value)} className="h-8 text-sm font-semibold border-0 bg-transparent px-1 focus-visible:ring-1" placeholder="Chapter title..." data-testid={`chapter-title-${i}`} />
                  <div className="flex items-center gap-1 shrink-0">
                    {hasAudio && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => playChapterAudio(i)} data-testid={`play-chapter-${i}`}>{playingChapter === i ? <Pause className="w-3.5 h-3.5 text-[#1A9DA5]" /> : <Play className="w-3.5 h-3.5 text-[#1A9DA5]" />}</Button>}
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveChapter(i, -1)} disabled={i === 0}><ChevronUp className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveChapter(i, 1)} disabled={i === chapters.length - 1}><ChevronDown className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeChapter(i)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Textarea value={ch.content || ''} onChange={e => updateChapter(i, 'content', e.target.value)} rows={6} className="text-sm" placeholder="Chapter script content..." data-testid={`chapter-content-${i}`} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                    <Input
                      type="number"
                      step="0.5"
                      min="0.5"
                      value={ch.target_length_minutes || ''}
                      onChange={e => updateChapter(i, 'target_length_minutes', e.target.value)}
                      className="h-7 w-28 text-xs"
                      placeholder="Length (min)"
                      data-testid={`chapter-length-${i}`}
                    />
                    <span className="text-xs text-muted-foreground">target minutes</span>
                  </div>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">{wordCount} words / ~{Math.round(wordCount / 145 * 10) / 10} min</Badge>
                      {hasAudio && <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-600"><AudioLines className="w-3 h-3 mr-1" />Audio ready</Badge>}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" onClick={() => generateChapterScript(i)} disabled={generatingChapter === i} data-testid={`ai-chapter-${i}`}>
                        {generatingChapter === i ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1 text-[#E8735A]" />}
                        {generatingChapter === i ? 'Writing...' : 'AI Write'}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => generateChapterTTS(i)} disabled={generatingTTS === i || !hasContent} data-testid={`tts-chapter-${i}`}>
                        {generatingTTS === i ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Volume2 className="w-3 h-3 mr-1 text-[#1A9DA5]" />}
                        {generatingTTS === i ? 'Generating...' : hasAudio ? 'Redo Audio' : 'Gen Audio'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          <Button variant="outline" className="w-full border-dashed" onClick={addChapter}><Plus className="w-4 h-4 mr-1" />Add Chapter</Button>
        </div>
      )}
    </div>
  );
}

/* ─── TEASER, PDF & EXTRAS ─── */
function TeaserAndExtras({ product, setProduct, productId, isNew }) {
  const [voices, setVoices] = useState({ openai: [], elevenlabs: [] });
  const [provider, setProvider] = useState('openai');
  const [voice, setVoice] = useState('alloy');
  const [model, setModel] = useState('tts-1-hd');
  const [speed, setSpeed] = useState(1.0);
  const [generatingTeaser, setGeneratingTeaser] = useState(false);
  const [generatingTeaserScript, setGeneratingTeaserScript] = useState(false);
  const [playingTeaser, setPlayingTeaser] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [analyzePdf, setAnalyzePdf] = useState(product.type === 'pdf');
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [showAnalysisDialog, setShowAnalysisDialog] = useState(false);
  const audioRef = React.useRef(null);
  const pdfInputRef = React.useRef(null);

  useEffect(() => {
    api.get('/tts/voices').then(r => {
      setVoices(r.data);
      if (r.data.openai?.length) setVoice(r.data.openai[0].id);
    }).catch(() => {});
  }, []);

  const update = (field, value) => setProduct(prev => ({ ...prev, [field]: value }));

  const generateTeaserScript = async () => {
    if (!productId) { toast.error('Save product first'); return; }
    setGeneratingTeaserScript(true);
    try {
      toast.info('Generating teaser script with AI...');
      const { data } = await api.post('/ai/generate-teaser-script', { product_id: productId });
      setProduct(prev => ({ ...prev, teaser_script: data.teaser_script }));
      toast.success(`Teaser script generated (${data.word_count} words)`);
    } catch (err) { toast.error(getErrorMsg(err, 'Generation failed')); }
    finally { setGeneratingTeaserScript(false); }
  };

  const generateTeaserAudio = async () => {
    if (!productId) { toast.error('Save product first'); return; }
    if (!product.teaser_script?.trim()) { toast.error('Add a teaser script first'); return; }
    setGeneratingTeaser(true);
    try {
      toast.info('Generating teaser audio...');
      const { data } = await api.post('/tts/generate-teaser', { product_id: productId, provider, voice, model, speed });
      setProduct(prev => ({ ...prev, teaser_audio_base64: data.audio_base64 }));
      toast.success('Teaser audio generated!');
    } catch (err) { toast.error(getErrorMsg(err, 'Teaser generation failed')); }
    finally { setGeneratingTeaser(false); }
  };

  const playTeaser = () => {
    if (!product.teaser_audio_base64) return;
    if (playingTeaser) { audioRef.current?.pause(); setPlayingTeaser(false); return; }
    if (audioRef.current) {
      audioRef.current.src = `data:audio/mp3;base64,${product.teaser_audio_base64}`;
      audioRef.current.play();
      setPlayingTeaser(true);
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.includes('pdf')) { toast.error('Please select a PDF file'); return; }
    if (!productId) { toast.error('Save product first'); return; }
    setUploadingPdf(true);
    try {
      const CHUNK_SIZE = 512 * 1024; // 512KB chunks
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      let uploadId = '';
      let result = null;

      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        const formData = new FormData();
        formData.append('file', chunk, file.name);
        formData.append('chunk_index', i.toString());
        formData.append('total_chunks', totalChunks.toString());
        formData.append('upload_id', uploadId);

        const { data } = await api.post(`/upload/pdf-chunk/${productId}`, formData);
        uploadId = data.upload_id;

        if (data.complete) {
          result = data;
        }
      }

      if (result) {
        setProduct(prev => ({ ...prev, pdf_asset_id: result.id, pdf_filename: result.filename, pdf_page_count: result.page_count }));
        toast.success(`PDF uploaded: ${result.filename} (${result.page_count} pages)`);

        if (analyzePdf) {
          startPdfAnalysis();
        }
      }
    } catch (err) { toast.error(getErrorMsg(err, 'Upload failed')); }
    finally { setUploadingPdf(false); if (e.target) e.target.value = ''; }
  };

  const startPdfAnalysis = async () => {
    if (!productId) return;
    setAnalyzing(true);
    try {
      toast.info('Starting PDF analysis...');
      const { data } = await api.post(`/ai/analyze-pdf/${productId}`);
      const jobId = data.job_id;
      const poll = async () => {
        try {
          const { data: job } = await api.get(`/ai/analyze-pdf/status/${jobId}`);
          if (job.status === 'complete') {
            setAnalysisResult(job.result);
            setShowAnalysisDialog(true);
            setAnalyzing(false);
          } else if (job.status === 'failed') {
            toast.error(job.error || 'PDF analysis failed');
            setAnalyzing(false);
          } else {
            setTimeout(poll, 3000);
          }
        } catch { setAnalyzing(false); toast.error('Failed to check analysis status'); }
      };
      setTimeout(poll, 3000);
    } catch (err) { toast.error(getErrorMsg(err, 'Failed to start analysis')); setAnalyzing(false); }
  };

  const applyAnalysis = () => {
    if (!analysisResult) return;
    setProduct(prev => ({
      ...prev,
      title: analysisResult.title || prev.title,
      subtitle: analysisResult.subtitle || prev.subtitle,
      short_description: analysisResult.short_description || prev.short_description,
      long_description: analysisResult.long_description || prev.long_description,
      problem_solved: analysisResult.problem_solved || prev.problem_solved,
      outcomes: analysisResult.outcomes || prev.outcomes,
      tags: analysisResult.tags || prev.tags,
      category: analysisResult.category || prev.category,
      difficulty: analysisResult.difficulty || prev.difficulty,
      faq: analysisResult.faq || prev.faq,
      target_business_types: analysisResult.target_business_types || prev.target_business_types,
      teaser_script: analysisResult.teaser_script || prev.teaser_script,
      outline: analysisResult.outline || prev.outline,
    }));
    setShowAnalysisDialog(false);
    toast.success('Product details filled from PDF analysis');
  };

  const removePdf = async () => {
    if (!productId) return;
    try {
      await api.delete(`/products/${productId}/pdf`);
      setProduct(prev => ({ ...prev, pdf_asset_id: '', pdf_filename: '' }));
      toast.success('PDF removed');
    } catch (err) { toast.error('Failed to remove PDF'); }
  };

  return (
    <>
      <audio ref={audioRef} onEnded={() => setPlayingTeaser(false)} />

      {/* PDF Document */}
      <Card data-testid="pdf-upload-card">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4 text-[#1A9DA5]" />Downloadable PDF</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">Upload a PDF document that customers can download.</p>

          {/* Analyze checkbox */}
          <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg border bg-muted/30">
            <Checkbox checked={analyzePdf} onCheckedChange={v => setAnalyzePdf(v)} />
            <div>
              <span className="text-xs font-medium">Analyze PDF and fill product details</span>
              <p className="text-[10px] text-muted-foreground">AI will extract content and suggest title, description, outcomes, FAQ, etc.</p>
            </div>
          </label>

          {product.pdf_asset_id && product.pdf_filename ? (
            <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
              <FileText className="w-8 h-8 text-red-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{product.pdf_filename}</p>
                <p className="text-xs text-muted-foreground">
                  {product.pdf_page_count ? `${product.pdf_page_count} pages · ` : ''}PDF document attached
                </p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="outline" size="sm" onClick={startPdfAnalysis} disabled={analyzing}>
                  {analyzing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1 text-[#1A9DA5]" />}
                  {analyzing ? 'Analyzing...' : 'Analyze'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => pdfInputRef.current?.click()} disabled={uploadingPdf || analyzing}>
                  {(uploadingPdf || analyzing) ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileUp className="w-3 h-3 mr-1" />}
                  Replace
                </Button>
                <Button variant="outline" size="sm" onClick={removePdf} className="text-destructive"><X className="w-3 h-3 mr-1" />Remove</Button>
              </div>
            </div>
          ) : (
            <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-[#1A9DA5]/50 transition-colors cursor-pointer" onClick={() => {
              if (isNew) { toast.error('Save the product first, then upload PDF'); return; }
              if (!uploadingPdf && !analyzing) pdfInputRef.current?.click();
            }}>
              {(uploadingPdf || analyzing) ? (
                <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-[#1A9DA5]" />
              ) : (
                <FileUp className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              )}
              <p className="text-sm font-medium">{analyzing ? 'Analyzing PDF...' : uploadingPdf ? 'Uploading...' : 'Click to upload PDF'}</p>
              <p className="text-xs text-muted-foreground">{isNew ? 'Save product first' : 'Max 50MB'}</p>
            </div>
          )}
          <input ref={pdfInputRef} type="file" accept=".pdf" className="hidden" onChange={handlePdfUpload} disabled={isNew || uploadingPdf || analyzing} />
        </CardContent>
      </Card>

      {/* PDF Analysis Dialog */}
      <Dialog open={showAnalysisDialog} onOpenChange={setShowAnalysisDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>PDF Content Analysis</DialogTitle></DialogHeader>
          {analysisResult && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">AI has analyzed the PDF content. Review and confirm to fill the product details.</p>
              <div className="space-y-2 text-sm max-h-[60vh] overflow-y-auto pr-1">
                <div className="p-3 rounded-lg border"><p className="text-xs font-medium text-muted-foreground">Title</p><p className="font-semibold">{analysisResult.title}</p></div>
                <div className="p-3 rounded-lg border"><p className="text-xs font-medium text-muted-foreground">Subtitle</p><p>{analysisResult.subtitle}</p></div>
                <div className="p-3 rounded-lg border"><p className="text-xs font-medium text-muted-foreground">Short Description</p><p>{analysisResult.short_description}</p></div>
                <div className="p-3 rounded-lg border"><p className="text-xs font-medium text-muted-foreground">Problem Solved</p><p>{analysisResult.problem_solved}</p></div>
                <div className="p-3 rounded-lg border"><p className="text-xs font-medium text-muted-foreground">Long Description</p><p className="text-xs whitespace-pre-wrap line-clamp-6">{analysisResult.long_description}</p></div>
                {analysisResult.outcomes?.length > 0 && (
                  <div className="p-3 rounded-lg border"><p className="text-xs font-medium text-muted-foreground">Outcomes ({analysisResult.outcomes.length})</p>
                    <ul className="list-disc pl-4 mt-1 space-y-0.5 text-xs">{analysisResult.outcomes.map((o, i) => <li key={i}>{o}</li>)}</ul>
                  </div>
                )}
                {analysisResult.tags?.length > 0 && (
                  <div className="p-3 rounded-lg border"><p className="text-xs font-medium text-muted-foreground">Tags</p><p className="text-xs">{analysisResult.tags.join(', ')}</p></div>
                )}
                {analysisResult.target_business_types?.length > 0 && (
                  <div className="p-3 rounded-lg border"><p className="text-xs font-medium text-muted-foreground">Target Audience</p><p className="text-xs">{analysisResult.target_business_types.join(', ')}</p></div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  {analysisResult.category && <div className="p-3 rounded-lg border"><p className="text-xs font-medium text-muted-foreground">Category</p><p className="text-xs">{analysisResult.category}</p></div>}
                  {analysisResult.difficulty && <div className="p-3 rounded-lg border"><p className="text-xs font-medium text-muted-foreground">Difficulty</p><p className="text-xs capitalize">{analysisResult.difficulty}</p></div>}
                </div>
                {analysisResult.teaser_script && (
                  <div className="p-3 rounded-lg border"><p className="text-xs font-medium text-muted-foreground">Teaser Script</p><p className="text-xs italic">{analysisResult.teaser_script}</p></div>
                )}
                {analysisResult.outline && (
                  <div className="p-3 rounded-lg border"><p className="text-xs font-medium text-muted-foreground">Outline</p><p className="text-xs whitespace-pre-wrap">{analysisResult.outline}</p></div>
                )}
                {analysisResult.key_topics?.length > 0 && (
                  <div className="p-3 rounded-lg border"><p className="text-xs font-medium text-muted-foreground">Key Topics</p><p className="text-xs">{analysisResult.key_topics.join(', ')}</p></div>
                )}
                {analysisResult.unique_selling_points?.length > 0 && (
                  <div className="p-3 rounded-lg border"><p className="text-xs font-medium text-muted-foreground">Unique Selling Points</p>
                    <ul className="list-disc pl-4 mt-1 space-y-0.5 text-xs">{analysisResult.unique_selling_points.map((u, i) => <li key={i}>{u}</li>)}</ul>
                  </div>
                )}
                {analysisResult.faq?.length > 0 && (
                  <div className="p-3 rounded-lg border"><p className="text-xs font-medium text-muted-foreground">FAQ ({analysisResult.faq.length})</p>
                    {analysisResult.faq.map((f, i) => <div key={i} className="mt-1"><p className="font-medium text-xs">{f.question}</p><p className="text-xs text-muted-foreground">{f.answer}</p></div>)}
                  </div>
                )}
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={applyAnalysis} className="flex-1 bg-[#1A9DA5] hover:bg-[#158D92]">Apply to Product</Button>
                <Button variant="outline" onClick={() => setShowAnalysisDialog(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Teaser Script & Audio */}
      <Card data-testid="teaser-card">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Volume2 className="w-4 h-4 text-[#1A9DA5]" />Teaser Script & Audio</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label>Teaser Script</Label>
              <Button variant="ghost" size="sm" onClick={generateTeaserScript} disabled={generatingTeaserScript || isNew} className="text-[#1A9DA5] hover:text-[#158D92]" data-testid="generate-teaser-script-btn">
                {generatingTeaserScript ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                {generatingTeaserScript ? 'Generating...' : product.teaser_script ? 'Regenerate Script' : 'AI Generate Script'}
              </Button>
            </div>
            <Textarea value={product.teaser_script} onChange={e => update('teaser_script', e.target.value)} rows={4} placeholder="The hook that makes people want to buy..." data-testid="teaser-script-input" />
          </div>

          {!isNew && (
            <div className="space-y-3">
              <Separator />
              <Label className="text-sm font-medium">Generate Teaser Audio</Label>
              <TTSSettings voices={voices} provider={provider} setProvider={setProvider} voice={voice} setVoice={setVoice} model={model} setModel={setModel} speed={speed} setSpeed={setSpeed} />
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={generateTeaserAudio} disabled={generatingTeaser || !product.teaser_script?.trim()} data-testid="generate-teaser-audio-btn">
                  {generatingTeaser ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Volume2 className="w-3.5 h-3.5 mr-1 text-[#1A9DA5]" />}
                  {generatingTeaser ? 'Generating...' : product.teaser_audio_base64 ? 'Regenerate Teaser Audio' : 'Generate Teaser Audio'}
                </Button>
                {product.teaser_audio_base64 && (
                  <Button variant="outline" size="sm" onClick={playTeaser} data-testid="play-teaser-btn">
                    {playingTeaser ? <Pause className="w-3.5 h-3.5 mr-1 text-[#1A9DA5]" /> : <Play className="w-3.5 h-3.5 mr-1 text-[#1A9DA5]" />}
                    {playingTeaser ? 'Pause' : 'Play Teaser'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Outline & Transcript */}
      <Card>
        <CardHeader><CardTitle className="text-base">Outline & Transcript</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1"><Label>Outline (chapter listing)</Label><Textarea value={product.outline} onChange={e => update('outline', e.target.value)} rows={5} placeholder="Chapter 1: Title&#10;Chapter 2: Title..." /></div>
          <Separator />
          <div className="space-y-1">
            <Label>Full Transcript (auto-assembled from chapters)</Label>
            <Textarea value={product.transcript_full || (product.chaptered_script || []).map(ch => `## ${ch.title}\n\n${ch.content || ''}`).join('\n\n---\n\n')} onChange={e => update('transcript_full', e.target.value)} rows={6} className="font-mono text-xs" />
            <p className="text-xs text-muted-foreground">This is auto-assembled from chapters. Edit only if you need a custom version.</p>
          </div>
        </CardContent>
      </Card>
    </>
  );
}

/* ─── MAIN EDITOR ─── */
export default function ProductEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const [product, setProduct] = useState({
    type: 'guide', title: '', subtitle: '', category: '', problem_solved: '', outcomes: [],
    target_business_types: [], difficulty: 'beginner', duration_minutes: 15, price: 29,
    tags: [], short_description: '', long_description: '', faq: [], testimonials: [],
    status: 'draft', outline: '', chaptered_script: [], teaser_script: '',
    transcript_full: '', transcript_teaser: '', included_guides: [],
    package_price: null, compare_at_price: null, savings_display: '', audio_url: '', teaser_audio_url: '',
    pdf_asset_id: '', pdf_filename: '', teaser_audio_base64: '',
    discounted_price: null, external_link_url: '', external_link_label: '',
    category_id: ''
  });
  const [saving, setSaving] = useState(false);
  const [generatingDesc, setGeneratingDesc] = useState(false);
  const [allGuides, setAllGuides] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [landingPages, setLandingPages] = useState([]);

  useEffect(() => {
    if (id) {
      api.get(`/products/${id}`).then(r => setProduct(r.data)).catch(() => toast.error('Product not found'));
      api.get(`/pages?product_id=${id}`).then(r => setLandingPages(r.data || [])).catch(() => {});
    }
    api.get('/products?type=guide').then(r => setAllGuides(r.data)).catch(() => {});
    api.get('/categories').then(r => setAllCategories(r.data || [])).catch(() => {});
  }, [id]);

  const update = (field, value) => setProduct(prev => ({ ...prev, [field]: value }));

  const generateDescription = async () => {
    if (!id) { toast.error('Save product first'); return; }
    if (!product.title?.trim()) { toast.error('Add a title first'); return; }
    setGeneratingDesc(true);
    try {
      toast.info('Generating description with AI...');
      const { data } = await api.post('/ai/generate-description', { product_id: id });
      setProduct(prev => ({
        ...prev,
        problem_solved: data.problem_solved || prev.problem_solved,
        short_description: data.short_description || prev.short_description,
        long_description: data.long_description || prev.long_description,
        outcomes: data.outcomes || prev.outcomes,
        tags: data.tags || prev.tags,
      }));
      toast.success('Description generated!');
    } catch (err) { toast.error(getErrorMsg(err, 'Generation failed')); }
    finally { setGeneratingDesc(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post('/products', product);
        toast.success('Product created');
        navigate(`/admin/products/${data.id}`);
      } else {
        await api.put(`/products/${id}`, product);
        toast.success('Product saved');
      }
    } catch (err) { toast.error(getErrorMsg(err, 'Save failed')); }
    finally { setSaving(false); }
  };

  const generateLandingPage = async () => {
    if (!id) { toast.error('Save product first'); return; }
    try {
      toast.info('Generating landing page with AI...');
      const { data } = await api.post('/ai/generate-landing-page', { product_id: id, target_audience: product.target_business_types?.join(', '), angle: 'save time', tone: 'friendly', template_type: 'problem_solution' });
      toast.success('Landing page generated!');
      navigate(`/admin/pages/${data.id}`);
    } catch { toast.error('Generation failed'); }
  };

  return (
    <div className="space-y-6" data-testid="product-editor">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/products')}><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="text-2xl font-bold font-['Outfit']">{isNew ? 'New Product' : 'Edit Product'}</h1>
            <p className="text-sm text-muted-foreground">{product.type === 'guide' ? 'Audio Guide' : 'Package'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {!isNew && <Button variant="outline" size="sm" onClick={generateLandingPage}><Sparkles className="w-4 h-4 mr-1 text-[#E8735A]" />Generate Landing Page</Button>}
          <Button data-testid="save-product-btn" size="sm" onClick={save} disabled={saving} className="bg-[#1A9DA5] hover:bg-[#158D92] text-white">
            <Save className="w-4 h-4 mr-1" />{saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Landing Pages */}
      {!isNew && landingPages.length > 0 && (
        <Card className="border-[#1A9DA5]/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold font-['Outfit'] flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5 text-[#1A9DA5]" />Landing Pages ({landingPages.length})
              </h3>
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={generateLandingPage}>
                <Sparkles className="w-3 h-3 mr-1 text-[#E8735A]" />Generate New
              </Button>
            </div>
            <div className="space-y-1.5">
              {landingPages.map(lp => (
                <div key={lp.id} className="flex items-center gap-2 p-2 rounded-lg border bg-muted/30 text-sm">
                  <Badge variant="outline" className={`text-[10px] h-5 ${lp.status === 'published' ? 'bg-emerald-500/10 text-emerald-600' : ''}`}>{lp.status}</Badge>
                  <span className="flex-1 truncate text-xs">{lp.title || lp.slug}</span>
                  <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" title="Copy link" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/l/${lp.slug}`); toast.success('Link copied'); }}>
                    <Copy className="w-3 h-3" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" title="Edit page" onClick={() => navigate(`/admin/pages/${lp.id}`)}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" title="Preview" onClick={() => window.open(`/l/${lp.slug}`, '_blank')}>
                    <ExternalLink className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="basic">
        <TabsList>
          <TabsTrigger value="basic">Basic Info</TabsTrigger>
          {product.type !== 'pdf' && <TabsTrigger value="chapters" data-testid="tab-chapters"><Headphones className="w-3.5 h-3.5 mr-1" />Chapters & Audio</TabsTrigger>}
          <TabsTrigger value="content">Teaser & Extras</TabsTrigger>
          <TabsTrigger value="pricing">Pricing & Status</TabsTrigger>
          {product.type === 'package' && <TabsTrigger value="package">Package</TabsTrigger>}
        </TabsList>

        {/* BASIC INFO TAB */}
        <TabsContent value="basic" className="mt-4 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-base">General</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1">
                  <Label>Type</Label>
                  <Select value={product.type} onValueChange={v => update('type', v)}>
                    <SelectTrigger data-testid="product-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="guide">Audio Guide</SelectItem><SelectItem value="pdf">PDF Guide</SelectItem><SelectItem value="package">Package</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label>Title</Label><Input data-testid="product-title" value={product.title} onChange={e => update('title', e.target.value)} placeholder="e.g., Master Your Cash Flow" /></div>
                <div className="space-y-1"><Label>Subtitle</Label><Input value={product.subtitle} onChange={e => update('subtitle', e.target.value)} /></div>
                <div className="space-y-1">
                  <Label>Category</Label>
                  <Select value={product.category_id || '__none__'} onValueChange={v => update('category_id', v === '__none__' ? '' : v)}>
                    <SelectTrigger data-testid="product-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No Category</SelectItem>
                      {allCategories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {product.category_id && (
                  <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg border bg-muted/30">
                    <Checkbox checked={!!product.featured_in_category} onCheckedChange={v => update('featured_in_category', v)} data-testid="featured-in-category-checkbox" />
                    <span className="text-xs font-medium">Featured in category</span>
                  </label>
                )}
                <div className="space-y-1"><Label>Category Tag <span className="text-xs text-muted-foreground">(display label)</span></Label><Input value={product.category} onChange={e => update('category', e.target.value)} placeholder="e.g., Finance, Marketing" /></div>
                <div className="space-y-1">
                  <Label>Difficulty</Label>
                  <Select value={product.difficulty} onValueChange={v => update('difficulty', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="beginner">Beginner</SelectItem><SelectItem value="intermediate">Intermediate</SelectItem><SelectItem value="advanced">Advanced</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label>Duration (minutes)</Label><Input type="number" value={product.duration_minutes} onChange={e => update('duration_minutes', parseInt(e.target.value) || 0)} /></div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-base">Description</CardTitle>
                <Button variant="ghost" size="sm" onClick={generateDescription} disabled={generatingDesc || isNew} className="text-[#1A9DA5] hover:text-[#158D92]" data-testid="generate-description-btn">
                  {generatingDesc ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                  {generatingDesc ? 'Generating...' : 'AI Generate'}
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1"><Label>Problem Solved</Label><Textarea value={product.problem_solved} onChange={e => update('problem_solved', e.target.value)} rows={2} /></div>
                <div className="space-y-1"><Label>Short Description</Label><Textarea value={product.short_description} onChange={e => update('short_description', e.target.value)} rows={2} /></div>
                <div className="space-y-1"><Label>Long Description</Label><Textarea value={product.long_description} onChange={e => update('long_description', e.target.value)} rows={4} /></div>
                <div className="space-y-1"><Label>Outcomes (one per line)</Label><Textarea value={(product.outcomes || []).join('\n')} onChange={e => update('outcomes', e.target.value.split('\n').filter(Boolean))} rows={3} /></div>
                <div className="space-y-1"><Label>Tags (comma-separated)</Label><Input value={(product.tags || []).join(', ')} onChange={e => update('tags', e.target.value.split(',').map(t => t.trim()).filter(Boolean))} /></div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* CHAPTERS & AUDIO TAB */}
        <TabsContent value="chapters" className="mt-4" data-testid="chapters-tab">
          {isNew ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground">
              <Headphones className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>Save the product first to manage chapters and generate audio.</p>
            </CardContent></Card>
          ) : (
            <ChapterManager product={product} setProduct={setProduct} productId={id} />
          )}
        </TabsContent>

        {/* TEASER & EXTRAS TAB */}
        <TabsContent value="content" className="mt-4 space-y-4">
          <TeaserAndExtras product={product} setProduct={setProduct} productId={id} isNew={isNew} />
        </TabsContent>

        {/* PRICING TAB */}
        <TabsContent value="pricing" className="mt-4 space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Pricing & Status</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1"><Label>Price ($)</Label><Input type="number" step="0.01" value={product.price} onChange={e => update('price', parseFloat(e.target.value) || 0)} data-testid="product-price-input" /></div>
                <div className="space-y-1">
                  <Label className="flex items-center gap-1">Discounted Price ($) <span className="text-xs text-muted-foreground">(optional)</span></Label>
                  <Input type="number" step="0.01" min="0" value={product.discounted_price ?? ''} onChange={e => { const v = e.target.value; update('discounted_price', v === '' ? null : parseFloat(v)); }} placeholder="Leave empty for no discount" data-testid="product-discounted-price-input" />
                  {product.discounted_price !== null && product.discounted_price !== undefined && product.discounted_price !== '' && (
                    <p className="text-xs text-[#1A9DA5]">
                      {parseFloat(product.discounted_price) === 0 ? 'Will display as "Get it for free now!"' : `Customers see $${parseFloat(product.discounted_price).toFixed(2)} with $${product.price} strikethrough`}
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label>Status</Label>
                  <Select value={product.status} onValueChange={v => update('status', v)}>
                    <SelectTrigger data-testid="product-status"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="published">Published</SelectItem><SelectItem value="archived">Archived</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <Separator />
              <div>
                <Label className="flex items-center gap-1 mb-1">External Resource Link <span className="text-xs text-muted-foreground">(optional)</span></Label>
                <p className="text-xs text-muted-foreground mb-2">Add a companion tool or resource link shown on the product page, landing page, and customer library.</p>
                <div className="grid grid-cols-2 gap-3">
                  <Input value={product.external_link_url || ''} onChange={e => update('external_link_url', e.target.value)} placeholder="https://example.com/tool" data-testid="product-external-link-url" />
                  <Input value={product.external_link_label || ''} onChange={e => update('external_link_label', e.target.value)} placeholder="Button label (default: Recommended Resource)" data-testid="product-external-link-label" />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PACKAGE TAB */}
        {product.type === 'package' && (
          <TabsContent value="package" className="mt-4 space-y-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Package Settings</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1"><Label>Package Price ($)</Label><Input type="number" step="0.01" value={product.package_price || ''} onChange={e => update('package_price', parseFloat(e.target.value) || null)} /></div>
                  <div className="space-y-1"><Label>Compare At ($)</Label><Input type="number" step="0.01" value={product.compare_at_price || ''} onChange={e => update('compare_at_price', parseFloat(e.target.value) || null)} /></div>
                  <div className="space-y-1"><Label>Savings Display</Label><Input value={product.savings_display} onChange={e => update('savings_display', e.target.value)} placeholder="e.g., Save $20" /></div>
                </div>
                <div className="space-y-2">
                  <Label>Included Guides</Label>
                  {allGuides.filter(g => g.id !== id).map(g => (
                    <label key={g.id} className="flex items-center gap-2 p-2 rounded border hover:bg-accent cursor-pointer">
                      <Switch checked={(product.included_guides || []).includes(g.id)} onCheckedChange={checked => {
                        const ids = product.included_guides || [];
                        update('included_guides', checked ? [...ids, g.id] : ids.filter(i => i !== g.id));
                      }} />
                      <span className="text-sm">{g.title}</span>
                      <Badge variant="outline" className="ml-auto">${g.price}</Badge>
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
